import { type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button, Card, CardSubtitle, CardTitle } from '@/shared/components/ui'

interface FallbackProps {
  error: unknown
  resetError: () => void
}

function ErrorFallback({ error, resetError }: FallbackProps) {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Error desconocido'

  return (
    <div className="grid min-h-screen place-items-center bg-surface p-6">
      <Card className="w-full max-w-lg space-y-4 text-center">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-danger-soft text-danger">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <CardTitle className="font-serif text-2xl font-normal">Algo salió mal</CardTitle>
        <CardSubtitle className="text-sm">{message}</CardSubtitle>
        <div className="flex justify-center gap-2 pt-2">
          <Button onClick={resetError} variant="primary">
            <RefreshCw className="h-4 w-4" />
            Reintentar
          </Button>
          <Button onClick={() => window.location.assign('/')} variant="outline">
            Ir al inicio
          </Button>
        </div>
        {import.meta.env.DEV && error instanceof Error && error.stack && (
          <pre className="mt-4 max-h-48 overflow-auto rounded bg-surface p-3 text-left text-[11px] text-ink-muted">
            {error.stack}
          </pre>
        )}
      </Card>
    </div>
  )
}

interface AppErrorBoundaryProps {
  children: ReactNode
  /** Render alternativo del fallback (por feature). Si no se pasa, usa el default. */
  fallback?: (props: FallbackProps) => ReactNode
}

/**
 * Wrapper de `Sentry.ErrorBoundary` con UI alineada al sistema de diseño TecoPOS.
 *
 * - Captura errores en el render tree y los reporta a Sentry automáticamente.
 * - Muestra fallback elegante (no la pantalla blanca de la muerte).
 * - En dev, expone el stack trace; en prod queda oculto.
 *
 * Úsalo en dos niveles:
 * 1. **Global**: alrededor de `<AppRouter />` en `AppProviders.tsx` — protege
 *    de errores no manejados que llegarían hasta React root.
 * 2. **Por feature** (opcional): alrededor de páginas pesadas (chatbot,
 *    estadísticas) — un error ahí no rompe el resto del portal.
 */
export function AppErrorBoundary({ children, fallback }: AppErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => {
        const props = { error, resetError }
        return fallback ? <>{fallback(props)}</> : <ErrorFallback {...props} />
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
